import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, fmtDate, todayIso } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, Send, Trash2, Calendar, MessageSquarePlus, Eye,
  ImageIcon, Download,
} from "lucide-react";
import { toast } from "sonner";
import RichEditor from "@/components/RichEditor";
import FileUploader from "@/components/FileUploader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  admin: "Süper Admin", sales: "Satış", accounting: "Muhasebe",
  warehouse: "Depo", viewer: "Görüntüleme",
};

const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

// Download-URL with token for image display (backend requires auth on /api/files/:id)
const authUrl = (url) => {
  const token = localStorage.getItem("azd_token");
  if (!url) return "";
  const full = url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  return `${full}${full.includes("?") ? "&" : "?"}auth=${token}`;
};

export default function ActivityReports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState("all");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [reportDate, setReportDate] = useState(todayIso());
  const [sending, setSending] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [comment, setComment] = useState("");

  const load = async () => {
    const params = {};
    if (isAdmin && filterUser !== "all") params.user_id = filterUser;
    const { data } = await api.get("/activity-reports", { params });
    setReports(data);
  };

  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data));
    load();
  }, [isAdmin, filterUser]);

  const send = async (e) => {
    e.preventDefault();
    if (!stripHtml(content)) {
      toast.error("Lütfen rapor içeriği yazın");
      return;
    }
    setSending(true);
    try {
      await api.post("/activity-reports", {
        title: title.trim(),
        content: content,
        images: images,
        report_date: reportDate,
      });
      toast.success("Faaliyet raporunuz patrona gönderildi");
      setTitle("");
      setContent("");
      setImages([]);
      setReportDate(todayIso());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gönderim başarısız");
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu raporu silmek istediğinize emin misiniz?")) return;
    await api.delete(`/activity-reports/${id}`);
    toast.success("Rapor silindi");
    setViewer(null);
    load();
  };

  const addComment = async () => {
    if (!comment.trim()) return toast.error("Yorum boş olamaz");
    try {
      const { data } = await api.post(`/activity-reports/${viewer.id}/comments`,
        { comment: comment.trim() });
      setViewer(data);
      setComment("");
      toast.success("Yorum eklendi");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Yorum eklenemedi");
    }
  };

  const removeComment = async (cid) => {
    if (!window.confirm("Yorum silinsin mi?")) return;
    await api.delete(`/activity-reports/${viewer.id}/comments/${cid}`);
    const updated = { ...viewer, admin_comments: (viewer.admin_comments || []).filter(c => c.id !== cid) };
    setViewer(updated);
    toast.success("Silindi");
  };

  return (
    <div className="space-y-5">
      {/* Compose new report - non-admins */}
      {!isAdmin && (
        <form onSubmit={send} className="azd-card p-6 max-w-5xl" data-testid="ar-compose">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-[#0B3676]/10 text-[#0B3676] flex items-center justify-center">
              <FileText size={18} />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-[#08285A]">Günlük Faaliyet Raporu</h2>
              <p className="text-xs text-[#6B7280]">
                Raporunuz {user?.full_name} adına yöneticiye iletilecek. Word benzeri araçlarla biçimlendirin, gerekirse görsel ekleyin.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Başlık (opsiyonel)</label>
              <Input data-testid="ar-title" value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder="Örn: Müşteri ziyaretleri ve günün özeti" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">
                Rapor Tarihi <span className="text-[#DC2626]">*</span>
              </label>
              <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)}
                     required data-testid="ar-date" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">
                Faaliyet İçeriği <span className="text-[#DC2626]">*</span>
              </label>
              <RichEditor value={content} onChange={setContent}
                          placeholder="Bugünkü faaliyetlerinizi madde madde yazın. Ctrl+B kalın, Ctrl+I italik, Ctrl+U altını çiz..."
                          minHeight={260} testid="ar-content" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">
                Görseller (opsiyonel — 25MB&apos;a kadar, maks 8)
              </label>
              <FileUploader value={images} onChange={setImages}
                            category="activity-reports"
                            accept="image/jpeg,image/png,image/webp,image/jpg"
                            maxFiles={8} maxSizeMB={25}
                            label="Görsel Yükle" testid="ar-images" />
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={sending} data-testid="ar-send"
                    className="bg-[#0B3676] hover:bg-[#08285A] text-white">
              <Send size={14} className="mr-1.5" />
              {sending ? "Gönderiliyor..." : "Patrona Gönder"}
            </Button>
          </div>
        </form>
      )}

      {/* Admin filter */}
      {isAdmin && (
        <div className="azd-card p-4 lg:p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-[#111827] mb-1.5">Personel Filtresi</label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger data-testid="ar-filter-user"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Personeller</SelectItem>
                  {users.filter(u => u.role !== "admin").map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-[#6B7280]">
              Toplam <span className="font-bold text-[#08285A]">{reports.length}</span> rapor
            </div>
          </div>
        </div>
      )}

      {/* Reports list */}
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-[#08285A] uppercase tracking-wider">
          {isAdmin ? "Personel Faaliyet Raporları" : "Gönderdiğim Raporlar"}
        </h3>
        {reports.length === 0 ? (
          <div className="azd-card p-8 text-center text-[#6B7280]">
            {isAdmin ? "Henüz gönderilmiş rapor bulunmuyor" : "Henüz rapor göndermediniz"}
          </div>
        ) : reports.map((r) => {
          const preview = stripHtml(r.content).slice(0, 220);
          const imgCount = (r.images || []).length;
          const cmtCount = (r.admin_comments || []).length;
          return (
            <div key={r.id} data-testid={`ar-${r.id}`}
                 onClick={() => setViewer(r)}
                 className="azd-card p-5 cursor-pointer hover:border-[#0B3676] transition-colors">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#08285A] text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {r.user_name?.[0] || "?"}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-[#08285A]">{r.user_name}</div>
                      <Badge variant="info">{ROLE_LABELS[r.user_role] || r.user_role}</Badge>
                      {imgCount > 0 && (
                        <Badge variant="default">
                          <ImageIcon size={10} className="inline mr-0.5" /> {imgCount}
                        </Badge>
                      )}
                      {cmtCount > 0 && (
                        <Badge variant="success">💬 {cmtCount} yorum</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#6B7280] mt-0.5">
                      <span className="flex items-center gap-1">
                        <Calendar size={11} /> Rapor: {fmtDate(r.report_date)}
                      </span>
                      <span>Gönderim: {fmtDateTime(r.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); setViewer(r); }}
                    className="p-1.5 text-[#6B7280] hover:text-[#0B3676] rounded"
                    title="Görüntüle" data-testid={`ar-view-${r.id}`}>
                    <Eye size={14} />
                  </button>
                  {(isAdmin || r.user_id === user?.id) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(r.id); }}
                      data-testid={`ar-delete-${r.id}`}
                      className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded"
                      title="Sil">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              {r.title && (
                <div className="font-semibold text-[#111827] mb-1">{r.title}</div>
              )}
              <div className="text-sm text-[#6B7280] line-clamp-2">
                {preview}{preview.length >= 220 && "…"}
              </div>
            </div>
          );
        })}
      </div>

      {/* Viewer modal — full report + comments */}
      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} className="text-[#0B3676]" />
              {viewer?.title || "Faaliyet Raporu"}
            </DialogTitle>
          </DialogHeader>
          {viewer && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7280] pb-3 border-b border-[#E5E7EB]">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#08285A] text-white flex items-center justify-center text-xs font-bold">
                    {viewer.user_name?.[0]}
                  </div>
                  <span className="font-bold text-[#08285A]">{viewer.user_name}</span>
                  <Badge variant="info">{ROLE_LABELS[viewer.user_role] || viewer.user_role}</Badge>
                </div>
                <span>Rapor Tarihi: <b>{fmtDate(viewer.report_date)}</b></span>
                <span>Gönderim: {fmtDateTime(viewer.created_at)}</span>
              </div>

              <RichEditor value={viewer.content} editable={false} minHeight={120} />

              {(viewer.images || []).length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-[#111827] mb-2">Eklenen Görseller</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {(viewer.images || []).map((img) => (
                      <a key={img.id} href={authUrl(img.download_url)} target="_blank"
                         rel="noopener noreferrer"
                         data-testid={`ar-img-${img.id}`}
                         className="group relative block border border-[#E5E7EB] rounded-lg overflow-hidden hover:border-[#0B3676] transition-colors">
                        <img src={authUrl(img.download_url)}
                             alt={img.original_filename}
                             loading="lazy"
                             className="w-full h-32 object-cover" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-semibold">
                          <Download size={16} className="mr-1" /> İndir
                        </div>
                        <div className="px-2 py-1 text-[10px] text-[#6B7280] truncate bg-white">
                          {img.original_filename}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments thread */}
              <div className="pt-3 border-t border-[#E5E7EB]">
                <div className="text-xs font-bold text-[#08285A] uppercase tracking-wider mb-3 flex items-center gap-2">
                  <MessageSquarePlus size={14} /> Yönetici Yorumları
                  <span className="text-[#6B7280] font-normal normal-case">
                    ({(viewer.admin_comments || []).length})
                  </span>
                </div>
                {(viewer.admin_comments || []).length === 0 ? (
                  <div className="text-sm text-[#6B7280] text-center py-3">
                    Henüz yorum eklenmedi.
                  </div>
                ) : (
                  <div className="space-y-2 mb-3">
                    {viewer.admin_comments.map((c) => (
                      <div key={c.id} data-testid={`ar-cmt-${c.id}`}
                           className="p-3 rounded-lg bg-[#F6F8FB] border-l-4 border-[#FFE600]">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="text-xs">
                            <span className="font-bold text-[#08285A]">{c.user_name}</span>
                            <span className="text-[#6B7280] ml-2">{fmtDateTime(c.created_at)}</span>
                          </div>
                          {isAdmin && (
                            <button onClick={() => removeComment(c.id)}
                                    className="text-[#6B7280] hover:text-[#DC2626]"
                                    title="Yorumu sil">
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                        <div className="text-sm text-[#111827] whitespace-pre-line">{c.comment}</div>
                      </div>
                    ))}
                  </div>
                )}
                {isAdmin && (
                  <div className="flex gap-2">
                    <Input value={comment} onChange={(e) => setComment(e.target.value)}
                           placeholder="Bu rapora yorum/not yazın..."
                           onKeyDown={(e) => e.key === "Enter" && addComment()}
                           data-testid="ar-cmt-input" />
                    <Button onClick={addComment} data-testid="ar-cmt-send"
                            className="bg-[#0B3676] hover:bg-[#08285A] text-white shrink-0">
                      Yorum Ekle
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewer(null)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
